import { useState, useCallback, useRef } from "react";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import Spinner from "@excalidraw/excalidraw/components/Spinner";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import { newTextElement } from "@excalidraw/element";
import { abysiusApi } from "../data/abysiusApi";
import { useAtom, atom } from "../app-jotai";
import { randomId } from "@excalidraw/common";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import "./PromptToText.scss";

// Atoms for dialog state
export const promptToTextDialogOpenAtom = atom(false);

interface PromptToTextDialogProps {
  excalidrawAPI: ExcalidrawImperativeAPI;
}

type TextPurpose =
  | "general"
  | "brainstorm"
  | "summarize"
  | "explain"
  | "translate"
  | "code";

const PURPOSE_PROMPTS: Record<TextPurpose, string> = {
  general: "",
  brainstorm: "Help me brainstorm ideas about: ",
  summarize: "Summarize the following: ",
  explain: "Explain in simple terms: ",
  translate: "Translate to English: ",
  code: "Write code for: ",
};

const PURPOSE_SYSTEM_PROMPTS: Record<TextPurpose, string> = {
  general: "You are a helpful assistant. Provide clear and concise responses.",
  brainstorm:
    "You are a creative brainstorming assistant. Generate diverse and innovative ideas. Present ideas as bullet points.",
  summarize:
    "You are a summarization expert. Provide concise summaries that capture the key points.",
  explain:
    "You are an expert teacher. Explain concepts clearly using simple language and helpful analogies.",
  translate:
    "You are a professional translator. Provide accurate and natural translations.",
  code: "You are an expert programmer. Write clean, well-commented code with explanations.",
};

export const PromptToTextDialog = ({
  excalidrawAPI,
}: PromptToTextDialogProps) => {
  const [isOpen, setIsOpen] = useAtom(promptToTextDialogOpenAtom);
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedText, setGeneratedText] = useState("");
  const [streamingText, setStreamingText] = useState("");

  // Options
  const [purpose, setPurpose] = useState<TextPurpose>("general");
  const [useStreaming, setUseStreaming] = useState(true);
  const [model, setModel] = useState("gpt-3.5-turbo");
  const [temperature, setTemperature] = useState(0.7);

  const abortControllerRef = useRef<AbortController | null>(null);

  const handleClose = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsOpen(false);
    setError(null);
    setGeneratedText("");
    setStreamingText("");
  }, [setIsOpen]);

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim() || isGenerating) {
      return;
    }

    if (!abysiusApi.isConfigured()) {
      setError("Abysius API key not configured. Please set up your API key.");
      return;
    }

    setIsGenerating(true);
    setError(null);
    setGeneratedText("");
    setStreamingText("");

    trackEvent("ai", "generate", "prompt-to-text");

    const fullPrompt = PURPOSE_PROMPTS[purpose] + prompt;
    const systemPrompt = PURPOSE_SYSTEM_PROMPTS[purpose];

    try {
      abortControllerRef.current = new AbortController();

      if (useStreaming) {
        const result = await abysiusApi.generateTextStream(
          fullPrompt,
          (chunk) => {
            setStreamingText((prev) => prev + chunk);
          },
          {
            model,
            systemPrompt,
            temperature,
          },
        );
        setGeneratedText(result);
        setStreamingText("");
      } else {
        const result = await abysiusApi.generateText(fullPrompt, {
          model,
          systemPrompt,
          temperature,
        });
        setGeneratedText(result);
      }

      trackEvent("ai", "generate success", "prompt-to-text");
    } catch (err: any) {
      console.error("Text generation error:", err);
      setError(err.message || "Failed to generate text");
      trackEvent("ai", "generate failed", "prompt-to-text");
    } finally {
      setIsGenerating(false);
      abortControllerRef.current = null;
    }
  }, [prompt, isGenerating, purpose, useStreaming, model, temperature]);

  const handleInsertText = useCallback(() => {
    const textToInsert = generatedText || streamingText;
    if (!textToInsert) {
      return;
    }

    try {
      const appState = excalidrawAPI.getAppState();

      // Calculate center of viewport
      const centerX =
        -appState.scrollX + appState.width / 2 / appState.zoom.value;
      const centerY =
        -appState.scrollY + appState.height / 2 / appState.zoom.value;

      // Create a text element
      const textElement = newTextElement({
        x: centerX - 200, // Offset to roughly center the text
        y: centerY - 50,
        text: textToInsert,
        fontSize: 16,
        fontFamily: 1,
        textAlign: "left",
        verticalAlign: "top",
        strokeColor: appState.currentItemStrokeColor,
        backgroundColor: "transparent",
        fillStyle: "solid",
        strokeWidth: 1,
        strokeStyle: "solid",
        roughness: 1,
        opacity: 100,
        roundness: null,
        locked: false,
        id: randomId(),
        containerId: null,
        originalText: textToInsert,
        autoResize: true,
        lineHeight: 1.25,
      });

      // Add the element to the scene
      const elements = excalidrawAPI.getSceneElements();
      excalidrawAPI.updateScene({
        elements: [...elements, textElement],
      });

      // Select the new element
      excalidrawAPI.updateScene({
        appState: {
          selectedElementIds: { [textElement.id]: true },
        },
      });

      trackEvent("ai", "insert text", "prompt-to-text");
      handleClose();
    } catch (err: any) {
      setError(`Failed to insert text: ${err.message}`);
    }
  }, [generatedText, streamingText, excalidrawAPI, handleClose]);

  const handleCopyText = useCallback(() => {
    const textToCopy = generatedText || streamingText;
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy);
      trackEvent("ai", "copy text", "prompt-to-text");
    }
  }, [generatedText, streamingText]);

  if (!isOpen) {
    return null;
  }

  const displayText = streamingText || generatedText;

  return (
    <Dialog
      className="prompt-to-text-dialog"
      onCloseRequest={handleClose}
      title="Prompt to Text (AI)"
      size={800}
    >
      <div className="prompt-to-text-content">
        <div className="options-row">
          <div className="option-group">
            <label>Purpose:</label>
            <select
              value={purpose}
              onChange={(e) => setPurpose(e.target.value as TextPurpose)}
              disabled={isGenerating}
            >
              <option value="general">General</option>
              <option value="brainstorm">Brainstorm Ideas</option>
              <option value="summarize">Summarize</option>
              <option value="explain">Explain</option>
              <option value="translate">Translate</option>
              <option value="code">Write Code</option>
            </select>
          </div>

          <div className="option-group">
            <label>Model:</label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              disabled={isGenerating}
            >
              <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
              <option value="gpt-4">GPT-4</option>
              <option value="gpt-4-turbo">GPT-4 Turbo</option>
            </select>
          </div>

          <div className="option-group">
            <label>Creativity:</label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={temperature}
              onChange={(e) => setTemperature(parseFloat(e.target.value))}
              disabled={isGenerating}
            />
            <span className="temp-value">{temperature.toFixed(1)}</span>
          </div>

          <div className="option-group checkbox">
            <label>
              <input
                type="checkbox"
                checked={useStreaming}
                onChange={(e) => setUseStreaming(e.target.checked)}
                disabled={isGenerating}
              />
              Stream response
            </label>
          </div>
        </div>

        <div className="prompt-section">
          <label htmlFor="text-prompt">
            {purpose !== "general" ? PURPOSE_PROMPTS[purpose] : "Your prompt:"}
          </label>
          <textarea
            id="text-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={
              purpose === "general"
                ? "Ask me anything..."
                : "Enter your text here..."
            }
            rows={4}
            disabled={isGenerating}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                handleGenerate();
              }
            }}
          />
          <div className="prompt-hint">
            Press <kbd>{navigator.platform.includes("Mac") ? "⌘" : "Ctrl"}</kbd>{" "}
            + <kbd>Enter</kbd> to generate
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="actions">
          <FilledButton
            onClick={handleGenerate}
            disabled={!prompt.trim() || isGenerating}
            label={isGenerating ? "Generating..." : "Generate"}
            icon={isGenerating ? <Spinner /> : undefined}
          />
        </div>

        {displayText && (
          <div className="generated-text">
            <div className="result-header">
              <h4>Generated Text:</h4>
              <div className="result-actions">
                <button onClick={handleCopyText} className="action-button">
                  📋 Copy
                </button>
                <button onClick={handleInsertText} className="action-button primary">
                  ✏️ Insert to Canvas
                </button>
              </div>
            </div>
            <div className="text-output">
              <pre>{displayText}</pre>
              {isGenerating && <span className="cursor-blink">▊</span>}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default PromptToTextDialog;
